import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearSession,
  fetchMe,
  isLoggedIn,
  login,
  register,
  startGoogleLoginWeb,
} from "./forge/suite/auth";
import { fetchWallet, startCheckout, subscribeMember } from "./forge/suite/wallet";
import type { SuiteUser, WalletSnapshot } from "./forge/suite/types";
import { catalogReplyHint } from "./chat/debit";
import { NO_TARGET_MSG } from "./chat/errors";
import {
  appendMessage,
  emptyThread,
  loadThreads,
  newId,
  patchMessage,
  mergeThreads,
  saveThreads,
  upsertThread,
} from "./chat/history";
import { houseTarget, refreshTargetForSend } from "./chat/resolve";
import { pullRemoteThreads, pushRemoteThreads } from "./chat/memory";
import { streamChat } from "./chat/stream";
import type { ChatThread, InferenceTarget } from "./chat/types";

const HOUSE_MEMORY_KEY = "house";

export function App() {
  const [user, setUser] = useState<SuiteUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [railOpen, setRailOpen] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [target, setTarget] = useState<InferenceTarget | null>(null);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const skipSaveRef = useRef(false);
  const email = user?.email || "guest";
  const modelKey = HOUSE_MEMORY_KEY;

  const refreshWallet = useCallback(async () => {
    const w = await fetchWallet();
    setWallet(w);
  }, []);

  useEffect(() => {
    setTarget(houseTarget());
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!isLoggedIn()) return;
    void fetchMe()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        if (!cancelled) clearSession();
      });
    void refreshWallet();
    return () => {
      cancelled = true;
    };
  }, [refreshWallet]);

  useEffect(() => {
    abortRef.current?.abort();
    skipSaveRef.current = true;
    const list = loadThreads(email, modelKey);
    setThreads(list);
    setActiveId(list[0]?.id || null);
    if (!isLoggedIn()) return;
    let cancelled = false;
    void pullRemoteThreads(modelKey)
      .then((remote) => {
        if (cancelled || !remote) return;
        setThreads((local) => {
          const merged = mergeThreads(local, remote);
          saveThreads(email, merged, modelKey);
          setActiveId((cur) =>
            cur && merged.some((t) => t.id === cur) ? cur : merged[0]?.id || null,
          );
          return merged;
        });
      })
      .catch(() => {
        /* bucket missing or origin down — local threads still work */
      });
    return () => {
      cancelled = true;
    };
  }, [email, modelKey]);

  useEffect(() => {
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    saveThreads(email, threads, modelKey);
    if (!isLoggedIn()) return;
    const t = window.setTimeout(() => {
      void pushRemoteThreads(threads, modelKey).catch(() => {});
    }, 1600);
    return () => window.clearTimeout(t);
  }, [email, modelKey, threads]);

  const active = useMemo(
    () => threads.find((t) => t.id === activeId) || null,
    [threads, activeId],
  );

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: "end" });
  }, [active?.messages, busy]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setSignInOpen(false);
      setWalletOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function ensureThread(): ChatThread {
    if (active) return active;
    const t = emptyThread(modelKey);
    setThreads((prev) => upsertThread(prev, t));
    setActiveId(t.id);
    return t;
  }

  function updateActive(next: ChatThread) {
    setThreads((prev) => upsertThread(prev, next));
    setActiveId(next.id);
  }

  async function send() {
    const text = draft.trim();
    if (!text || busy) return;
    if (!target) {
      setError(NO_TARGET_MSG);
      return;
    }
    if (!isLoggedIn()) {
      setSignInOpen(true);
      setError("Sign in to chat.");
      return;
    }
    if ((wallet?.balanceUsd ?? 0) <= 0 && target.source === "catalog") {
      setError("Add credits, then retry.");
      setWalletOpen(true);
      return;
    }
    setDraft("");
    setError("");
    setStatus("");
    let thread = ensureThread();
    const userMsg = { id: newId(), role: "user" as const, content: text, createdAt: Date.now() };
    thread = appendMessage(thread, userMsg);
    const asstId = newId();
    thread = appendMessage(thread, {
      id: asstId,
      role: "assistant",
      content: "",
      createdAt: Date.now(),
    });
    updateActive(thread);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    try {
      const payload = thread.messages
        .filter((m) => m.id !== asstId)
        .map((m) => ({ role: m.role, content: m.content }));
      await streamChat(refreshTargetForSend(target), payload, {
        signal: ctrl.signal,
        onToken: (tok) => {
          setThreads((prev) => {
            const cur = prev.find((t) => t.id === thread.id);
            if (!cur) return prev;
            const msg = cur.messages.find((m) => m.id === asstId);
            const next = patchMessage(cur, asstId, (msg?.content || "") + tok);
            return upsertThread(prev, next);
          });
        },
      });
      void refreshWallet();
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        setStatus("Stopped.");
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  function newChat() {
    const t = emptyThread(modelKey);
    setThreads((prev) => upsertThread(prev, t));
    setActiveId(t.id);
    setDraft("");
    setError("");
  }

  function removeThread(id: string) {
    setThreads((prev) => {
      const next = prev.filter((t) => t.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
  }

  async function buy(usd: number) {
    try {
      const url = await startCheckout(usd);
      if (url) window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function member() {
    try {
      const url = await subscribeMember();
      if (url) window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="cf-app">
      <header className="cf-header" data-agent="shell-header">
        <button type="button" className="cf-btn ghost" onClick={() => setRailOpen((v) => !v)}>
          {railOpen ? "Hide" : "Chats"}
        </button>
        <span className="cf-brand">ChatForge</span>
        <span className="cf-model">{target ? target.label : "House chat"}</span>
        <button type="button" className="cf-btn" onClick={newChat}>
          New chat
        </button>
        <button
          type="button"
          className="cf-btn"
          onClick={() => (isLoggedIn() ? setWalletOpen((v) => !v) : setSignInOpen(true))}
        >
          {isLoggedIn()
            ? `$${Number(wallet?.balanceUsd || 0).toFixed(2)}`
            : "Sign in"}
        </button>
        {isLoggedIn() ? (
          <button
            type="button"
            className="cf-btn ghost"
            onClick={() => {
              clearSession();
              setUser(null);
              setWallet(null);
            }}
          >
            Out
          </button>
        ) : null}
      </header>

      {walletOpen && isLoggedIn() ? (
        <div className="cf-pop">
          <div className="cf-pop-title">
            Wallet {wallet?.subscriptionActive ? "· Member" : ""}
          </div>
          <div className="cf-packs">
            {(wallet?.packs || [{ amountUsd: 5, label: "$5" }]).map((p) => (
              <button
                key={p.amountUsd}
                type="button"
                className="cf-btn"
                onClick={() => void buy(p.amountUsd)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {!wallet?.subscriptionActive ? (
            <button type="button" className="cf-btn primary" style={{ marginTop: 8 }} onClick={() => void member()}>
              Member $2.99
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="cf-body">
        <aside
          className={`cf-rail${railOpen ? "" : " collapsed"}`}
          data-agent="shell-sidebar"
        >
          <div className="cf-rail-head">
            <button type="button" className="cf-btn primary" onClick={newChat}>
              New chat
            </button>
            <div className="cf-hint" style={{ padding: "4px 8px" }}>
              Chats
            </div>
          </div>
          <div className="cf-thread-list" data-agent="rail">
            {threads.map((t) => (
              <div key={t.id} className="cf-thread-row">
                <button
                  type="button"
                  className={`cf-thread${t.id === activeId ? " active" : ""}`}
                  onClick={() => setActiveId(t.id)}
                >
                  {t.title}
                </button>
                <button type="button" className="cf-x" onClick={() => removeThread(t.id)} aria-label="Delete">
                  ×
                </button>
              </div>
            ))}
          </div>
        </aside>

        <main className="cf-main">
          <div className="cf-thread-pane" data-agent="primary">
            {!active || active.messages.length === 0 ? (
              <div className="cf-empty">
                <h2>Ask anything</h2>
                <p>
                  {isLoggedIn()
                    ? "Replies are billed to your suite wallet after the model speaks."
                    : "Sign in to chat."}
                </p>
              </div>
            ) : (
              active.messages.map((m) => (
                <div key={m.id} className={`cf-msg ${m.role}`}>
                  <div className="who">{m.role === "user" ? "You" : "Assistant"}</div>
                  <div className="bubble">{m.content || (busy && m.role === "assistant" ? "…" : "")}</div>
                </div>
              ))
            )}
            {status ? <div className="cf-status">{status}</div> : null}
            {error ? <div className="cf-status err">{error}</div> : null}
            <div ref={threadEndRef} />
          </div>
          <div className="cf-composer" data-agent="composer">
            <div className="cf-composer-inner">
              <textarea
                rows={1}
                value={draft}
                placeholder={isLoggedIn() ? "Message" : "Sign in to chat"}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
              {busy ? (
                <button type="button" className="cf-btn" onClick={stop}>
                  Stop
                </button>
              ) : (
                <button
                  type="button"
                  className="cf-btn primary"
                  disabled={!draft.trim()}
                  onClick={() => void send()}
                >
                  Send
                </button>
              )}
            </div>
            <div className="cf-hint">
              {target
                ? `${catalogReplyHint(target.source)} · Enter to send`
                : "Sign in to chat"}
            </div>
          </div>
        </main>
      </div>

      {signInOpen ? (
        <SignIn
          onClose={() => setSignInOpen(false)}
          onDone={(me) => {
            setUser(me);
            setSignInOpen(false);
            void refreshWallet();
          }}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

function SignIn(props: {
  onClose: () => void;
  onDone: (user: SuiteUser) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function go(kind: "login" | "register") {
    setBusy(true);
    try {
      const data =
        kind === "login"
          ? await login({ email, password })
          : await register({ email, password });
      props.onDone(data.user);
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cf-modal-back" onClick={(e) => e.target === e.currentTarget && props.onClose()}>
      <div className="cf-modal">
        <h3>VividLex sign in</h3>
        <label>
          Email
          <input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        <div className="cf-modal-actions">
          <button type="button" className="cf-btn primary" disabled={busy} onClick={() => void go("login")}>
            Sign in
          </button>
          <button type="button" className="cf-btn" disabled={busy} onClick={() => void go("register")}>
            Register
          </button>
          <button type="button" className="cf-btn" onClick={() => startGoogleLoginWeb()}>
            Continue with Google
          </button>
          <button type="button" className="cf-btn ghost" onClick={props.onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
